import { useAuthStore } from "@/features/auth/useAuthStore";
import { City, Country, Language, Venue, Zone, Currency } from "@/types/generated/GeoMasterDataApi";
import { createHttpInstance } from "../utils/http/http";
import { emit } from "@rsql/emitter";
import { AxiosInstance } from "axios";
import builder from "@rsql/builder";
import { Options } from "@/types/Http";

function getVenuesByCityCode(http: AxiosInstance) {
  return async (cityCodes: string[]) => {
    const { data } = await http.get<Venue[]>("/venues", {
      params: {
        filters: emit(builder.in("city", cityCodes)),
      },
    });

    return { data };
  };
}

function getVenues(http: AxiosInstance) {
  return async () => {
    const { data } = await http.get<Venue[]>("venues");

    return { data };
  };
}

function getZonesByAsxId(http: AxiosInstance) {
  return async (asxReceptiveId: string, options?: Options) => {
    const { config } = options ?? {};

    const filters = emit(builder.eq("asterix_receptive_id", asxReceptiveId));

    const { data } = await http.get<Zone[]>("zones", {
      ...config,
      params: { filters },
    });

    return { data };
  };
}

function getZones(http: AxiosInstance) {
  return async (params?: { search?: string; asxReceptiveId?: number }, options?: Options) => {
    const { config } = options ?? {};

    const filters = [];
    const CONTAINS = "=re=";

    if (params?.search) {
      filters.push(builder.comparison("zone_description", CONTAINS, `.*${params.search}.*`));
    }

    if (params?.asxReceptiveId) {
      filters.push(builder.eq("asterix_receptive_id", params.asxReceptiveId));
    }

    const combinedFilters = filters.length > 0 ? emit(builder.and(...filters)) : undefined;

    const { data } = await http.get<Zone[]>("zones", {
      ...config,
      params: { limit: params?.search ? 500 : 100, filters: combinedFilters },
    });

    return { data };
  };
}

function getZonesByIds(http: AxiosInstance) {
  return async (zoneIds: string[], options?: Options) => {
    const { config } = options ?? {};
    const filters = emit(builder.in("zone_id", zoneIds));

    const { data } = await http.get<Zone[]>("zones", { ...config, params: { filters } });
    return { data };
  };
}

function getLanguages(http: AxiosInstance) {
  return async (options?: Options) => {
    const { config } = options ?? {};

    const { data } = await http.get<Language[]>("languages", {
      ...config,
    });

    return { data };
  };
}

function getCities(http: AxiosInstance) {
  return async () => {
    return http.get<City[]>("cities");
  };
}

function getCountries(http: AxiosInstance) {
  return async () => {
    return http.get<Country[]>("countries");
  };
}

function getCurrencies(http: AxiosInstance) {
  return async (options?: Options) => {
    const { config } = options ?? {};
    return http.get<Currency[]>("currencies", {
      ...config,
    });
  };
}

export function useGeoMasterDataApi() {
  const runtimeConfig = useRuntimeConfig();
  const auth = useAuthStore();

  const axiosInstance = createHttpInstance({
    config: {
      baseURL: runtimeConfig.public.GEO_MASTER_DATA_BASE_URL,
      headers: {
        "accept-version": "vnd.geo-master-data-offer-service.v1",
      },
      params: { language_code: "en" },
    },
    auth,
  });

  return {
    _axiosInstance: axiosInstance,
    getVenues: getVenues(axiosInstance),
    getVenuesByCityCode: getVenuesByCityCode(axiosInstance),
    getZonesByAsxId: getZonesByAsxId(axiosInstance),
    getZones: getZones(axiosInstance),
    getZonesByIds: getZonesByIds(axiosInstance),
    getCities: getCities(axiosInstance),
    getCountries: getCountries(axiosInstance),
    getLanguages: getLanguages(axiosInstance),
    getCurrencies: getCurrencies(axiosInstance),
  };
}
